import _ from 'lodash';
import { useMemo, useEffect, forwardRef, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ImageContainer } from './ImageContainer.jsx';
import { VideoContainer } from './VideoContainer.jsx';
import { THREE_SIXTY_PREVIEW_VIDEOS } from '../config/threeSixty.constants';
import { THREE_SIXTY_DEFAULT_VIDEO_TEMPLATE_ID, THREE_SIXTY_DEFAULT_VIDEO_TRANSITION } from '../config/event.constants';
import { getThreeSixtyPreview } from '../../actions/threeSixtyPreview.js';
import Loading from '../../components/webapp/Loading.js';

const ThreeSixtyVideosDemo = forwardRef((props, ref) => {
  const dispatch = useDispatch();

  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  const {
    aspectRatio = '9:16',
    intro,
    outro,
    videoTransition,
    videoTemplate: {
      id: videoTemplateId = THREE_SIXTY_DEFAULT_VIDEO_TEMPLATE_ID,
      segments = [], // Ensure we get the segments array or an empty fallback
    },
    soundtrack,
    overlays,
  } = useSelector(
    (state) =>
      _.pick(state.proposalData.data.captureConfig?.modeConfiguration?.threeSixtyConfiguration, [
        'aspectRatio',
        'intro',
        'outro',
        'videoTransition',
        'videoTemplate',
        'soundtrack',
        'overlays',
      ]),
    _.isEqual
  );

  const overlayId = overlays?.[0]?.overlay?.[0];
  const overlayUrl = useSelector((state) => {
    return _.isNil(overlayId) ? null : state.proposalOverlays.data[overlayId]?.imageUrl;
  });

  const previewUrl = useSelector((state) => state.threeSixtyPreview.data);

  const { mainUrl, mainDuration } = useMemo(() => {
    const { video: mainUrl, duration: mainDuration } = THREE_SIXTY_PREVIEW_VIDEOS[aspectRatio][videoTemplateId] ?? {};
    return { mainUrl, mainDuration };
  }, [aspectRatio, videoTemplateId]);

  // Helper function to calculate slowestPlaybackSpeed for a segment
  const getSlowestPlaybackSpeed = (segment) => {
    const playbackSpeeds = segment.vfx
      .filter((effect) => effect.type === 'playbackSpeed')
      .map((effect) => effect.speed);

    return playbackSpeeds.length > 0 ? Math.min(...playbackSpeeds) : 100;
  };

  // The speed of the last segment in this video template
  // - a number between 25 (for 25%) and 200 (for 200%)
  const lastSegmentPlaybackSpeed = useMemo(() => {
    if (segments.length === 0) {
      return 100;
    } // Default to 100 if no segments

    const lastSegment = segments[segments.length - 1];
    return Math.round(getSlowestPlaybackSpeed(lastSegment)); // Get slowest playback speed from last segment
  }, [segments]);

  useEffect(() => {
    if (_.isNil(mainUrl)) {
      return;
    }

    // For Fiesta provided soundtracks, we have refined drop times, however,
    // we don't have these for customer uploaded soundtracks.
    // We want to play these soundtracks from the beggining, which can be achieved
    // by adding the intro duration and the video template drop time together.
    const soundtrackDropTime = soundtrack?.dropTime ?? (intro?.duration ?? 0) + 3.0; // This won't be a constant in the future.

    dispatch(
      getThreeSixtyPreview({
        aspectRatio,
        mainUrl,
        introUrl: intro?.originalUrl,
        outroUrl: outro?.originalUrl,
        soundtrackUrl: soundtrack?.soundtrackUrl,
        overlayUrl,
        introDuration: intro?.duration,
        soundtrackDuration: soundtrack?.duration,
        totalDuration: (intro?.duration ?? 0) + mainDuration + (outro?.duration ?? 0),
        videoTemplateDropTime: 3.0, // This won't be a constant in the future.
        soundtrackDropTime,
        outroDuration: outro?.duration,
        videoTransition: videoTransition ?? THREE_SIXTY_DEFAULT_VIDEO_TRANSITION,
        lastSegmentPlaybackSpeed,
      })
    );
  }, [
    aspectRatio,
    mainDuration,
    mainUrl,
    intro?.duration,
    intro?.originalUrl,
    outro?.duration,
    outro?.originalUrl,
    soundtrack?.soundtrackUrl,
    soundtrack?.dropTime,
    _.isNil(overlays?.[0]),
    overlayUrl,
  ]);

  const handleToggleVolume = useCallback(() => {
    setIsAudioPlaying((isAudioPlaying) => !isAudioPlaying);
  }, []);

  const videoContainerStyles = useMemo(() => {
    if (aspectRatio === '9:16') {
      return {
        display: 'flex',
        aspectRatio: (9 / 16).toString(),
        height: 480,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'black',
        position: 'relative',
        minWidth: 0,
      };
    }

    if (aspectRatio === '16:9') {
      return {
        display: 'flex',
        aspectRatio: (16 / 9).toString(),
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'black',
        position: 'relative',
        minWidth: 0,
      };
    }

    return {
      display: 'flex',
      aspectRatio: '1',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'black',
      position: 'relative',
      minWidth: 0,
    };
  }, [aspectRatio]);

  const videoStyles = useMemo(() => {
    if (aspectRatio === '9:16') {
      return {
        display: 'flex',
        aspectRatio: (9 / 16).toString(),
        height: 480,
        position: 'absolute',
      };
    }

    if (aspectRatio === '16:9') {
      return {
        display: 'flex',
        aspectRatio: (16 / 9).toString(),
        width: '100%',
        position: 'absolute',
      };
    }

    return {
      display: 'flex',
      aspectRatio: '1',
      width: '100%',
      position: 'absolute',
    };
  }, [aspectRatio]);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
      {_.isNil(previewUrl) ? (
        <Loading />
      ) : (
        <div style={videoContainerStyles}>
          <VideoContainer
            url={previewUrl}
            isLooping={true}
            isAutoPlay={true}
            isPlaying={true}
            resizeMode="contain"
            isMuted={!isAudioPlaying}
            style={videoStyles}
          />
          <div
            style={{ position: 'absolute', zIndex: 50, cursor: 'pointer', right: '24px', bottom: '24px' }}
            onClick={handleToggleVolume}
          >
            <ImageContainer
              source={isAudioPlaying ? '/static/proposal/unmuted.png' : '/static/proposal/muted.png'}
              style={{ width: '36px', height: '36px', backgroundSize: 'contain' }}
            />
          </div>
        </div>
      )}
    </div>
  );
});

export default ThreeSixtyVideosDemo;
